import { createSeed } from '../data/seed';
import { DEFAULT_PREFERENCES, ACCENT_MAP, TEXT_SIZES, SPACING } from '../constants/options';
import { refreshSession } from '../api/authClient';
import { getSyncStatus, onBoardReconciled, saveBoard } from '../api/storage';
import {
  addList,
  addPage,
  addTask,
  addTasks,
  deleteList,
  deletePage,
  deleteTaskById,
  moveTask,
  renameList,
  renamePage,
  reorderLists,
  reorderTask,
  toggleTaskDone,
  rollUnfinished,
  saveRecurring,
  setTaskColor,
  updateTaskNotes,
  updateTaskText,
} from '../api/board';
import { createContext, useContext, useEffect, useLayoutEffect, useMemo, useReducer, useRef } from 'react';
import { normalizeLang } from '../i18n/messages';
import { toKey } from '../utils/dates';

const AppContext = createContext(null);

function snapshotOf(state) {
  const { undo, ...rest } = state;
  if (typeof structuredClone === 'function') return structuredClone(rest);
  return JSON.parse(JSON.stringify(rest));
}

function withUndo(state, next, message) {
  if (next === state) return state;
  return { ...next, undo: { message, snapshot: snapshotOf(state) } };
}

function initState({ account, saved, rollSave }) {
  const seed = createSeed();
  const savedName = saved?.profile?.name;
  const name = savedName && savedName !== 'You' ? savedName : account?.name || seed.profile.name;
  const base = !saved
    ? { ...seed, profile: { ...seed.profile, name } }
    : {
        ...seed,
        ...saved,
        preferences: {
          ...DEFAULT_PREFERENCES,
          ...(saved.preferences || {}),
          title: 'Zippax',
          sidebarOpen: DEFAULT_PREFERENCES.sidebarOpen,
          celebrations: true,
        },
        profile: { ...seed.profile, ...(saved.profile || {}), name },
        pages: Array.isArray(saved.pages) && saved.pages.length ? saved.pages : seed.pages,
        days: saved.days || {},
        recurring: Array.isArray(saved.recurring) ? saved.recurring : [],
        undo: null,
      };
  const next = rollUnfinished(base, toKey(new Date()));
  if (rollSave) rollSave.current = next !== base;
  return next;
}

function reducer(state, action) {
  switch (action.type) {
    case 'SET_PREF':
      return {
        ...state,
        preferences: { ...state.preferences, [action.key]: action.value },
      };
    case 'TOGGLE_PREF':
      return {
        ...state,
        preferences: {
          ...state.preferences,
          [action.key]: !state.preferences[action.key],
        },
      };
    case 'SET_OFFSET':
      return { ...state, dayOffset: action.offset };
    case 'SHIFT_OFFSET':
      return { ...state, dayOffset: state.dayOffset + action.delta };
    case 'ROLL_FORWARD':
      return rollUnfinished(state, action.today);
    case 'ADD_TASK':
      return addTask(state, action.loc, action.text).state;
    case 'SAVE_RECURRING':
      return saveRecurring(state, action.item);
    case 'ADD_TASKS':
      return addTasks(state, action.loc, action.lines).state;
    case 'UPDATE_TASK':
      return updateTaskText(state, action.loc, action.id, action.text);
    case 'SET_TASK_COLOR':
      return setTaskColor(state, action.loc, action.id, action.color);
    case 'UPDATE_NOTES':
      return updateTaskNotes(state, action.loc, action.id, action.notes);
    case 'TOGGLE_TASK':
      return toggleTaskDone(state, action.loc, action.id, {
        completedToEnd: state.preferences.completedPosition === 'end',
      });
    case 'DELETE_TASK':
      return withUndo(state, deleteTaskById(state, action.loc, action.id), 'undo.task');
    case 'MOVE_TASK':
      return moveTask(state, action.source, action.target);
    case 'REORDER_TASK':
      return reorderTask(state, action.loc, action.index, action.direction);
    case 'ADD_PAGE':
      return addPage(state, action.id);
    case 'RENAME_PAGE':
      return renamePage(state, action.id, action.name);
    case 'DELETE_PAGE':
      return withUndo(state, deletePage(state, action.id), 'undo.page');
    case 'SELECT_PAGE':
      return { ...state, activePageId: action.id };
    case 'ADD_LIST':
      return addList(state, action.pageId, action.id);
    case 'RENAME_LIST':
      return renameList(state, action.pageId, action.listId, action.title);
    case 'REORDER_LISTS':
      return reorderLists(state, action.pageId, action.fromIndex, action.toIndex);
    case 'DELETE_LIST':
      return withUndo(
        state,
        deleteList(state, action.pageId, action.listId),
        'undo.list'
      );
    case 'SET_LISTS_EXPANDED':
      return { ...state, listsExpanded: action.value };
    case 'SET_LISTS_HEIGHT':
      return {
        ...state,
        listsHeight: Math.min(680, Math.max(180, Math.round(action.height))),
      };
    case 'SET_PROFILE':
      return { ...state, profile: { ...state.profile, name: action.name } };
    case 'JOIN_WAITLIST':
      return { ...state, profile: { ...state.profile, waitlist: true } };
    case 'RESET_CONTENT': {
      const seed = createSeed();
      return {
        ...seed,
        preferences: state.preferences,
        profile: state.profile,
        undo: null,
      };
    }
    case 'REPLACE_BOARD':
      return { ...state, ...action.board, undo: null };
    case 'UNDO':
      if (!state.undo?.snapshot) return state;
      return { ...state.undo.snapshot, undo: null };
    case 'CLEAR_UNDO':
      return state.undo ? { ...state, undo: null } : state;
    default:
      return state;
  }
}

export function AppProvider({ children, account, savedBoard }) {
  const rollSave = useRef(false);
  const [state, dispatch] = useReducer(
    reducer,
    { account, saved: savedBoard, rollSave },
    initState
  );
  const skipSave = useRef(true);
  const stateRef = useRef(state);
  stateRef.current = state;

  const actions = useMemo(
    () => ({
      setPref: (key, value) => dispatch({ type: 'SET_PREF', key, value }),
      togglePref: (key) => dispatch({ type: 'TOGGLE_PREF', key }),
      setOffset: (offset) => dispatch({ type: 'SET_OFFSET', offset }),
      shiftOffset: (delta) => dispatch({ type: 'SHIFT_OFFSET', delta }),
      addTask: (loc, text) => dispatch({ type: 'ADD_TASK', loc, text }),
      saveRecurring: (item) => dispatch({ type: 'SAVE_RECURRING', item }),
      addTasks: (loc, lines) => dispatch({ type: 'ADD_TASKS', loc, lines }),
      updateTask: (loc, id, text) => dispatch({ type: 'UPDATE_TASK', loc, id, text }),
      setTaskColor: (loc, id, color) => dispatch({ type: 'SET_TASK_COLOR', loc, id, color }),
      updateNotes: (loc, id, notes) => dispatch({ type: 'UPDATE_NOTES', loc, id, notes }),
      toggleTask: (loc, id) => dispatch({ type: 'TOGGLE_TASK', loc, id }),
      deleteTask: (loc, id) => dispatch({ type: 'DELETE_TASK', loc, id }),
      move: (source, target) => dispatch({ type: 'MOVE_TASK', source, target }),
      reorder: (loc, index, direction) =>
        dispatch({ type: 'REORDER_TASK', loc, index, direction }),
      addPage: (id) => dispatch({ type: 'ADD_PAGE', id }),
      renamePage: (id, name) => dispatch({ type: 'RENAME_PAGE', id, name }),
      deletePage: (id) => dispatch({ type: 'DELETE_PAGE', id }),
      selectPage: (id) => dispatch({ type: 'SELECT_PAGE', id }),
      addList: (pageId, id) => dispatch({ type: 'ADD_LIST', pageId, id }),
      renameList: (pageId, listId, title) =>
        dispatch({ type: 'RENAME_LIST', pageId, listId, title }),
      reorderLists: (pageId, fromIndex, toIndex) =>
        dispatch({ type: 'REORDER_LISTS', pageId, fromIndex, toIndex }),
      deleteList: (pageId, listId) => dispatch({ type: 'DELETE_LIST', pageId, listId }),
      setListsExpanded: (value) => dispatch({ type: 'SET_LISTS_EXPANDED', value }),
      setListsHeight: (height) => dispatch({ type: 'SET_LISTS_HEIGHT', height }),
      setProfile: (name) => dispatch({ type: 'SET_PROFILE', name }),
      joinWaitlist: () => dispatch({ type: 'JOIN_WAITLIST' }),
      resetContent: () => dispatch({ type: 'RESET_CONTENT' }),
      undo: () => dispatch({ type: 'UNDO' }),
    }),
    []
  );

  useEffect(() => {
    const persistRoll = rollSave.current;
    if (skipSave.current) {
      skipSave.current = false;
      rollSave.current = false;
      if (!persistRoll) return undefined;
    }
    const id = window.setTimeout(() => {
      saveBoard(state, account?.id);
    }, 400);
    return () => window.clearTimeout(id);
  }, [state, account?.id]);

  useEffect(() => {
    return onBoardReconciled((board) => dispatch({ type: 'REPLACE_BOARD', board }));
  }, []);

  useEffect(() => {
    if (!account?.id) return undefined;
    const sync = () => {
      refreshSession().finally(() => saveBoard(stateRef.current, account.id));
    };
    window.addEventListener('online', sync);
    const id = window.setInterval(() => {
      if (getSyncStatus() === 'offline') sync();
    }, 20000);
    return () => {
      window.removeEventListener('online', sync);
      window.clearInterval(id);
    };
  }, [account?.id]);

  useEffect(() => {
    let day = toKey(new Date());
    const rollIfNewDay = () => {
      const today = toKey(new Date());
      if (today === day) return;
      day = today;
      dispatch({ type: 'ROLL_FORWARD', today });
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') rollIfNewDay();
    };
    document.addEventListener('visibilitychange', onVisible);
    const id = window.setInterval(rollIfNewDay, 30000);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.clearInterval(id);
    };
  }, []);

  useEffect(() => {
    if (!state.undo) return undefined;
    const id = window.setTimeout(() => dispatch({ type: 'CLEAR_UNDO' }), 5000);
    return () => window.clearTimeout(id);
  }, [state.undo]);

  useLayoutEffect(() => {
    const root = document.documentElement;
    const { theme, accent, spacing, textSize, language } = state.preferences;
    const lang = normalizeLang(language);
    root.dataset.theme = theme;
    root.lang = lang;
    root.dir = lang === 'ar' ? 'rtl' : 'ltr';
    try {
      localStorage.setItem('teuxdeux.lang', lang);
      localStorage.setItem('teuxdeux.theme', theme);
    } catch {
      /* ignore */
    }
    root.style.setProperty('--accent', ACCENT_MAP[accent] || ACCENT_MAP.red);
    root.style.setProperty('--row', SPACING[spacing] || SPACING.m);
    root.style.setProperty('--task-size', TEXT_SIZES[textSize] || TEXT_SIZES.m);
  }, [state.preferences]);

  useEffect(() => {
    const onKey = (event) => {
      const target = event.target;
      const typing =
        target instanceof HTMLElement &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable);

      if (event.key === 'Escape') {
        if (document.activeElement instanceof HTMLElement) {
          document.activeElement.blur();
        }
      }

      if (event.code === 'Slash' && !typing && !event.metaKey && !event.ctrlKey) {
        event.preventDefault();
        document.dispatchEvent(new CustomEvent('deux:search'));
      }

      if ((event.metaKey || event.ctrlKey) && event.code === 'Backslash') {
        event.preventDefault();
        dispatch({ type: 'TOGGLE_PREF', key: 'sidebarOpen' });
      }

      if (typing || event.metaKey || event.ctrlKey || event.altKey) return;
      if (event.code === 'KeyT') dispatch({ type: 'SET_OFFSET', offset: 0 });
      if (event.code === 'BracketLeft') dispatch({ type: 'SHIFT_OFFSET', delta: -1 });
      if (event.code === 'BracketRight') dispatch({ type: 'SHIFT_OFFSET', delta: 1 });
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const value = useMemo(() => ({ state, actions }), [state, actions]);

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
  const context = useContext(AppContext);
  if (!context) throw new Error('useApp must be used within AppProvider');
  return context;
}
